/**
 * SPARQL editor (spec §15.1): CodeMirror 6 with the Lezer SPARQL grammar, term completion,
 * hover tooltips, `Mod-Enter` to run. Unbound (explorer, own history) or bound to a LoroText
 * (`graph_view` block; undo is the page-level manager, spec §8.7).
 *
 * VERIFY resolved: `codemirror-lang-sparql` 2.1 (MIT, Lezer grammar, 2026-04) exports `sparql()`;
 * preferred over the legacy stream mode.
 */
import { autocompletion, type CompletionContext, type CompletionResult } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, hoverTooltip, keymap, lineNumbers } from "@codemirror/view";
import { sparql } from "codemirror-lang-sparql";
import type { LoroDoc, LoroText } from "loro-crdt";
import { api } from "../api/client";
import { loroTextBinding } from "../editors/loroText";
import { parseIri } from "../lib/iri";
import { compact, declaredPrefixes, expandTerm, withMissingPrefixes } from "./prefixes";

export interface SparqlEditorOptions {
  initial?: string;
  doc?: LoroDoc;
  text?: LoroText;
  readOnly?: boolean;
  workspaceId?: string;
  prefixes(): Record<string, string>;
  onRun(query: string): void;
  onChange?(value: string): void;
}

export interface SparqlEditorHandle {
  view: EditorView;
  value(): string;
  insertPrefixes(): void;
  destroy(): void;
}

interface TermHit {
  iri: string;
  label: string;
  kind: string;
  definition?: string | null;
}

async function fetchTerms(q: string, ns: string): Promise<TermHit[]> {
  const params = new URLSearchParams({ q, limit: "30" });
  if (ns) params.set("ns", ns);
  const r = await fetch(`/api/ontology/terms?${params}`, { credentials: "same-origin" });
  return r.ok ? ((await r.json()) as TermHit[]) : [];
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function completionSource(opts: SparqlEditorOptions) {
  return async (ctx: CompletionContext): Promise<CompletionResult | null> => {
    const pname = ctx.matchBefore(/[A-Za-z][\w-]*:[\w.-]*$/);
    const iriTok = ctx.matchBefore(/<[^<>\s]*$/);
    if (!pname && !iriTok) return null;
    const query = ctx.state.doc.toString();
    const known = { ...opts.prefixes(), ...declaredPrefixes(query) };
    let search = "";
    let ns = "";
    let from = 0;
    if (pname) {
      const [prefix, local = ""] = pname.text.split(":");
      ns = known[prefix!] ?? "";
      if (!ns) return null;
      search = local;
      from = pname.from;
    } else if (iriTok) {
      search = iriTok.text.slice(1);
      if (search.length < 3 && !ctx.explicit) return null;
      from = iriTok.from;
    }
    await sleep(150); // debounce (spec §15.1 item 3)
    if (ctx.aborted) return null;
    const hits = await fetchTerms(search, ns);
    if (ctx.aborted) return null;
    return {
      from,
      options: hits.map((t) => ({
        label: pname ? compact(t.iri, known) : `<${t.iri}>`,
        detail: `${t.label} · ${t.kind}`,
        info: t.definition ? t.definition.slice(0, 300) : t.iri,
        type: t.kind === "property" ? "property" : "class",
      })),
    };
  };
}

function tokenAt(text: string, off: number): { from: number; to: number; token: string } | null {
  for (const re of [/<[^<>\s]*>/g, /[A-Za-z][\w-]*:[A-Za-z_0-9][\w.-]*/g]) {
    for (const m of text.matchAll(re)) {
      const start = m.index ?? 0;
      if (off >= start && off <= start + m[0].length) return { from: start, to: start + m[0].length, token: m[0] };
    }
  }
  return null;
}

async function describeIri(iri: string, workspaceId?: string): Promise<{ title: string; body: string } | null> {
  const parsed = parseIri(iri);
  if (parsed?.kind === "block" && workspaceId) {
    const r = await api.POST("/api/graph/describe-nodes", { body: { workspaceId, iris: [iri], langs: ["en"] } });
    const d = (r.data as Array<{ label: string; blockType?: string | null }> | undefined)?.[0];
    return d ? { title: d.label, body: `${d.blockType ?? "block"} · ${iri}` } : null;
  }
  const path = encodeURI(iri).replace(/#/g, "%23").replace(/\?/g, "%3F");
  const r = await fetch(`/api/ontology/terms/${path}`, { credentials: "same-origin" });
  if (!r.ok) return null;
  const t = (await r.json()) as TermHit & { deprecated?: boolean };
  return { title: `${t.label}${t.deprecated ? " (deprecated)" : ""}`, body: t.definition ?? iri };
}

function hover(opts: SparqlEditorOptions): Extension {
  return hoverTooltip(async (view, pos) => {
    const line = view.state.doc.lineAt(pos);
    const tok = tokenAt(line.text, pos - line.from);
    if (!tok) return null;
    const iri = expandTerm(tok.token, view.state.doc.toString(), opts.prefixes());
    if (!iri) return null;
    const info = await describeIri(iri, opts.workspaceId).catch(() => null);
    if (!info) return null;
    return {
      pos: line.from + tok.from,
      end: line.from + tok.to,
      above: true,
      create() {
        const dom = document.createElement("div");
        dom.className = "max-w-sm p-2 text-xs";
        dom.dataset.testid = "sparql-hover";
        const title = document.createElement("div");
        title.className = "font-semibold";
        title.textContent = info.title;
        const body = document.createElement("div");
        body.className = "text-neutral-600";
        body.textContent = info.body;
        dom.append(title, body);
        return { dom };
      },
    };
  });
}

export function mountSparqlEditor(parent: HTMLElement, opts: SparqlEditorOptions): SparqlEditorHandle {
  const bound = !!(opts.doc && opts.text);
  const run = (v: EditorView) => {
    opts.onRun(v.state.doc.toString());
    return true;
  };
  const view = new EditorView({
    parent,
    state: EditorState.create({
      doc: bound ? opts.text!.toString() : (opts.initial ?? ""),
      extensions: [
        lineNumbers(),
        sparql(),
        keymap.of([{ key: "Mod-Enter", run, preventDefault: true }]),
        bound ? [] : [history(), keymap.of(historyKeymap)],
        keymap.of([indentWithTab, ...defaultKeymap.filter((b) => !["Mod-z", "Mod-y", "Mod-Shift-z"].includes(b.key ?? ""))]),
        autocompletion({ override: [completionSource(opts)], activateOnTyping: true }),
        hover(opts),
        EditorState.readOnly.of(!!opts.readOnly),
        EditorView.editable.of(!opts.readOnly),
        bound ? loroTextBinding(opts.doc!, opts.text!) : [],
        EditorView.updateListener.of((u) => {
          if (u.docChanged) opts.onChange?.(u.state.doc.toString());
        }),
        EditorView.theme({ "&": { fontSize: "0.85rem" }, ".cm-content": { fontFamily: "ui-monospace, monospace" } }),
      ],
    }),
  });
  return {
    view,
    value: () => view.state.doc.toString(),
    insertPrefixes() {
      const current = view.state.doc.toString();
      const next = withMissingPrefixes(current, opts.prefixes());
      if (next !== current) view.dispatch({ changes: { from: 0, to: 0, insert: next.slice(0, next.length - current.length) } });
    },
    destroy: () => view.destroy(),
  };
}
